import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import { usePresetTableSearchParams } from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { LegacyRoute } from "../../../legacyRoutes";
import { ClassificationAndRights } from "../types";

import { ActionCell } from "./ClassificationActionCell";
import {
    CLASSIFICATION_LIST_COLUMN_IDS as ID,
    CLASSIFICATION_LIST_TABLE_STATE_STORAGE_KEY,
    classificationListTableDefaultValues,
} from "./consts";

export function useClassificationsListTable(
    classifications: Array<ClassificationAndRights>
) {
    const { t } = useTranslation();
    const columns = buildColumns(t);

    const notificationLabel = t("Table_RemovedFiltersWarning");
    const stateAndOnChanges = usePresetTableSearchParams(
        CLASSIFICATION_LIST_TABLE_STATE_STORAGE_KEY,
        classificationListTableDefaultValues,
        {},
        notificationLabel,
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: classifications,
        columns,
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ClassificationAndRights>(),
        getSortedRowModel: getSortedRowModel<ClassificationAndRights>(),
        getFilteredRowModel: getFilteredRowModel<ClassificationAndRights>(),
        getPaginationRowModel: getPaginationRowModel<ClassificationAndRights>(),
    });

    return { table };
}

const columnHelper = createColumnHelper<ClassificationAndRights>();

function buildColumns(t: TFunction) {
    return [
        columnHelper.accessor((row) => row.classification.title, {
            id: ID.NAME,
            header: () => t("Classification_List_Column_Name"),
            cell: (info) => (
                <ClassificationNameLink
                    classificationId={info.row.original.classification.id}
                    name={info.getValue()}
                />
            ),
            enableSorting: true,
            sortingFn: "alphanumeric",
            // Reachable via global search only; no per-column filter UI.
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.classification.description ?? "", {
            id: ID.DESCRIPTION,
            header: () => t("Classification_List_Column_Description"),
            cell: (info) => info.getValue(),
            enableSorting: true,
            sortingFn: "alphanumeric",
            // Reachable via global search only; no per-column filter UI.
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.display({
            id: ID.ACTION,
            header: () => t("Classification_List_Column_Action"),
            cell: (info) => (
                <ActionCell classificationAndRights={info.row.original} />
            ),
            enableSorting: false,
            size: TST_COLUMN_SIZE.L,
        }),
    ];
}

interface ClassificationNameLinkProps {
    classificationId: string;
    name: string;
}

function ClassificationNameLink({
    classificationId,
    name,
}: ClassificationNameLinkProps) {
    const url = `${LegacyRoute.REPORT_CONTROL_RESULT_STATUS}/?classification&classificationId=${classificationId}`;
    return <Link to={url}>{name}</Link>;
}
